import React, { useState } from 'react';
import { Coffee, Gamepad2, Scroll, Users, Calendar, Clock, CheckCircle, MapPin } from 'lucide-react';

type ReservationType = 'cafe' | 'gaming' | 'rpg' | null;

export const ReservationPage: React.FC = () => {
  const [selectedType, setSelectedType] = useState<ReservationType>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    // Zde by bylo volání API
  };

  if (formSubmitted) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white p-10 rounded-sm shadow-xl text-center border border-gray-100">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">Rezervace Odeslána</h2>
          <p className="text-gray-600 mb-8 font-sans">
            Děkujeme! Vaši rezervaci jsme přijali. Během chvíle vám dorazí potvrzovací SMS a e-mail.
          </p>
          <button 
            onClick={() => {setFormSubmitted(false); setSelectedType(null);}}
            className="text-guild-gold font-bold uppercase tracking-widest text-xs hover:underline"
          >
            Vytvořit další rezervaci
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfbf7] pt-20 pb-20">
      
      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 text-center mb-16 mt-10">
        <span className="text-guild-gold uppercase tracking-[0.2em] text-xs font-bold block mb-4">
            Plánování návštěvy
        </span>
        <h1 className="text-4xl md:text-6xl font-display font-bold text-gray-900 mb-6">Rezervace</h1>
        <p className="text-gray-500 font-sans text-lg max-w-2xl mx-auto font-light">
          Vyberte si prostor, který nejlépe vyhovuje vašim plánům. Ať už jde o rande u kávy, turnaj v Warhammeru nebo výpravu do podzemí.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-6">
        
        {/* Selection Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {/* Option: Cafe */}
          <button 
            onClick={() => setSelectedType('cafe')}
            className={`p-8 rounded-sm border-2 transition-all duration-300 text-left group relative overflow-hidden
              ${selectedType === 'cafe' 
                ? 'bg-white border-guild-gold shadow-xl scale-105 z-10' 
                : 'bg-white border-gray-100 hover:border-guild-gold/30 hover:shadow-lg'
              }`}
          >
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-colors
               ${selectedType === 'cafe' ? 'bg-guild-gold text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-guild-gold/10 group-hover:text-guild-gold'}`}>
              <Coffee size={28} />
            </div>
            <h3 className="text-xl font-display font-bold text-gray-900 mb-2">Stůl v Kavárně</h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              Pro pracovní schůzky, snídaně nebo kávu s přáteli. Klidnější prostředí.
            </p>
            {selectedType === 'cafe' && <div className="absolute top-4 right-4 text-guild-gold"><CheckCircle size={20} /></div>}
          </button>

          {/* Option: Gaming Table */}
          <button 
            onClick={() => setSelectedType('gaming')}
            className={`p-8 rounded-sm border-2 transition-all duration-300 text-left group relative overflow-hidden
              ${selectedType === 'gaming' 
                ? 'bg-gray-900 border-guild-gold shadow-xl scale-105 z-10' 
                : 'bg-white border-gray-100 hover:border-gray-900 hover:shadow-lg'
              }`}
          >
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-colors
               ${selectedType === 'gaming' ? 'bg-guild-gold text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-gray-800 group-hover:text-white'}`}>
              <Gamepad2 size={28} />
            </div>
            <h3 className={`text-xl font-display font-bold mb-2 ${selectedType === 'gaming' ? 'text-white' : 'text-gray-900'}`}>Herní Stůl</h3>
            <p className={`text-sm leading-relaxed ${selectedType === 'gaming' ? 'text-gray-400' : 'text-gray-500'}`}>
              Velké stoly pro deskovky nebo Wargaming (6x4"). Živější atmosféra.
            </p>
            {selectedType === 'gaming' && <div className="absolute top-4 right-4 text-guild-gold"><CheckCircle size={20} /></div>}
          </button>

          {/* Option: RPG Room */}
          <button 
            onClick={() => setSelectedType('rpg')}
            className={`p-8 rounded-sm border-2 transition-all duration-300 text-left group relative overflow-hidden
              ${selectedType === 'rpg' 
                ? 'bg-[#2a1a1a] border-guild-gold shadow-xl scale-105 z-10' 
                : 'bg-white border-gray-100 hover:border-[#2a1a1a] hover:shadow-lg'
              }`}
          >
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-colors
               ${selectedType === 'rpg' ? 'bg-guild-gold text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-[#4a2a2a] group-hover:text-white'}`}>
              <Scroll size={28} />
            </div>
            <h3 className={`text-xl font-display font-bold mb-2 ${selectedType === 'rpg' ? 'text-white' : 'text-gray-900'}`}>RPG Místnost</h3>
            <p className={`text-sm leading-relaxed ${selectedType === 'rpg' ? 'text-gray-400' : 'text-gray-500'}`}>
              Soukromá "Kobka" pro D&D a nerušené hraní. Odhlučněno.
            </p>
            {selectedType === 'rpg' && <div className="absolute top-4 right-4 text-guild-gold"><CheckCircle size={20} /></div>}
          </button>
        </div>

        {/* Form Section */}
        <div className={`transition-all duration-700 ease-in-out ${selectedType ? 'opacity-100 translate-y-0' : 'opacity-50 translate-y-10 grayscale pointer-events-none'}`}>
          <div className="bg-white border border-gray-200 shadow-xl rounded-sm overflow-hidden">
            <div className="bg-gray-50 p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-gray-800 uppercase tracking-widest text-sm flex items-center gap-2">
                {selectedType === 'cafe' && <><Coffee size={16} /> Detaily rezervace: Kavárna</>}
                {selectedType === 'gaming' && <><Gamepad2 size={16} /> Detaily rezervace: Herna</>}
                {selectedType === 'rpg' && <><Scroll size={16} /> Detaily rezervace: RPG Místnost</>}
                {!selectedType && "Vyberte typ rezervace výše"}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Jméno a Příjmení</label>
                    <input type="text" required className="w-full bg-gray-50 border border-gray-200 p-4 rounded-sm focus:outline-none focus:border-guild-gold transition-colors font-sans" placeholder="Jan Novák" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                     <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Datum</label>
                        <div className="relative">
                          <input type="date" required className="w-full bg-gray-50 border border-gray-200 p-4 rounded-sm focus:outline-none focus:border-guild-gold transition-colors font-sans" />
                          <Calendar className="absolute right-4 top-4 text-gray-400 pointer-events-none" size={18} />
                        </div>
                     </div>
                     <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Čas</label>
                        <div className="relative">
                          <input type="time" required className="w-full bg-gray-50 border border-gray-200 p-4 rounded-sm focus:outline-none focus:border-guild-gold transition-colors font-sans" />
                          <Clock className="absolute right-4 top-4 text-gray-400 pointer-events-none" size={18} />
                        </div>
                     </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Počet osob</label>
                    <div className="relative">
                        <select className="w-full bg-gray-50 border border-gray-200 p-4 rounded-sm focus:outline-none focus:border-guild-gold transition-colors appearance-none font-sans">
                          {[1,2,3,4,5,6,7,8, "9+ (Nutná domluva)"].map(n => <option key={n}>{n}</option>)}
                        </select>
                        <Users className="absolute right-4 top-4 text-gray-400 pointer-events-none" size={18} />
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Telefon</label>
                    <input type="tel" required className="w-full bg-gray-50 border border-gray-200 p-4 rounded-sm focus:outline-none focus:border-guild-gold transition-colors font-sans" placeholder="+420 777 123 456" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Email</label>
                    <input type="email" required className="w-full bg-gray-50 border border-gray-200 p-4 rounded-sm focus:outline-none focus:border-guild-gold transition-colors font-sans" placeholder="jan@example.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Poznámka</label>
                    <textarea 
                        rows={3} 
                        className="w-full bg-gray-50 border border-gray-200 p-4 rounded-sm focus:outline-none focus:border-guild-gold transition-colors font-sans resize-none" 
                        placeholder={selectedType === 'gaming' ? "Chci si zahrát Warhammer, potřebuji velký stůl..." : "Máme kočárek, alergie na lepek..."}
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                  <div className="text-xs text-gray-400 hidden md:block">
                      Rezervaci držíme 15 minut po smluveném čase.<br/>
                      Pro zrušení prosím volejte předem.
                  </div>
                  <button type="submit" className="bg-guild-gold text-white px-10 py-4 uppercase tracking-widest font-bold hover:bg-gray-900 transition-colors shadow-lg rounded-sm w-full md:w-auto">
                      Potvrdit Rezervaci
                  </button>
              </div>
            </form>
          </div>
        </div>

        {/* Location Info */}
        <div className="mt-16 text-center text-gray-500 text-sm">
             <p className="flex items-center justify-center gap-2 mb-2"><MapPin size={16} className="text-guild-gold" /> Náměstí Míru 15, Bruntál</p>
             <p>Potřebujete poradit? Volejte <strong>+420 777 123 456</strong></p>
        </div>

      </div>
    </div>
  );
};